import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AiWriteButtonProps {
  /** Context passed to the AI (occasion, recipient, purpose, etc.) */
  context: {
    occasion?: string;
    recipientName?: string;
    senderName?: string;
  };
  /** Describes what kind of text to generate (e.g. "group_invite", "personal_wish") */
  fieldPurpose?: string;
  onGenerated: (text: string) => void;
}

export function AiWriteButton({ context, fieldPurpose, onGenerated }: AiWriteButtonProps) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const generate = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-message", {
        body: {
          occasion: context.occasion || "celebration",
          recipientName: context.recipientName || "the recipient",
          senderName: context.senderName || "",
          fieldPurpose: fieldPurpose || "personal_wish",
        },
      });
      if (error) throw error;
      if (data?.message) onGenerated(data.message);
    } catch {
      toast({
        variant: "destructive",
        title: "AI generation failed",
        description: "Please try again or write your own message.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={generate}
      disabled={loading}
      className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
    >
      {loading ? (
        <Loader2 size={14} className="animate-spin" />
      ) : (
        <Sparkles size={14} />
      )}
      {loading ? "Writing..." : "AI Write"}
    </button>
  );
}
